import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import ChessNav from './ChessNav';
import Icon from '../Shared/Icons';
import { uploadToCloudinary } from '../../config/cloudinary';
import { C, FONT } from '../../utils/chessTheme';

const PLAYER_KEY = 'iyanna_chess_player';
function myId() {
  try { const raw = localStorage.getItem(PLAYER_KEY); return raw ? JSON.parse(raw).id : null; }
  catch { return null; }
}

// ── Hall of Fame — "the room of champions" ─────────────────────────────
// Walking into a gallery: portraits of TOURNAMENT champions hang in a row
// you scroll east → west. Only winning a tournament writes a name here —
// nothing weekly, nothing temporary. Tap a portrait and it steps forward,
// filling the screen with the champion's clean, professional badge. A
// champion can add their own portrait to their frame.

function fmtDate(ts) {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

function initials(name) {
  return (name || '?').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

export default function ChessHallOfFame() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // entry object
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const me = myId();

  useEffect(() => {
    const q = query(collection(db, 'chessHallOfFame'), orderBy('wonAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // Count titles per champion — repeat champions wear it on their badge
  const titleCounts = {};
  entries.forEach(e => {
    if (e.championId) titleCounts[e.championId] = (titleCounts[e.championId] || 0) + 1;
  });

  async function handlePhotoPick(e) {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      await updateDoc(doc(db, 'chessHallOfFame', selected.id), { photoURL: url });
      setSelected(s => ({ ...s, photoURL: url }));
    } catch (err) { console.error(err); }
    setUploading(false);
  }

  return (
    <div style={styles.page}>
      {/* Room atmosphere: soft spotlight from above */}
      <div style={styles.spotlight} />

      <div style={styles.bar}>
        <button style={styles.backIconBtn} onClick={() => navigate('/chess')}>
          <Icon name="door" size={19} color={C.text} strokeWidth={2.4} />
        </button>
        <div style={styles.barTitle}>Mabingwa</div>
        <div style={{ width: '38px' }} />
      </div>

      {loading ? (
        <div style={styles.empty}>Inapakia…</div>
      ) : entries.length === 0 ? (
        <div style={styles.emptyCard}>
          <Icon name="crown" size={30} color={C.textFaint} />
          <div style={styles.emptyText}>
            Kuta bado tupu. Bingwa wa tournament ya kwanza atatundikwa hapa
            milele — je, atakuwa wewe?
          </div>
          <Link to="/chess/tournaments" style={styles.playLink}>Tazama Tournaments →</Link>
        </div>
      ) : (
        <>
          {/* THE GALLERY WALL — horizontal, east to west */}
          <div style={styles.gallery}>
            {entries.map(e => {
              const titles = titleCounts[e.championId] || 1;
              return (
                <button key={e.id} style={styles.frame} onClick={() => setSelected(e)}>
                  <div style={styles.oval}>
                    {e.photoURL ? (
                      <img src={e.photoURL} alt={e.championName} style={styles.ovalImg} />
                    ) : (
                      <span style={styles.ovalInitials}>{initials(e.championName)}</span>
                    )}
                  </div>
                  <div style={styles.frameName}>{e.championName}</div>
                  <div style={styles.frameMeta}>{e.tournamentName}</div>
                  {titles > 1 && <div style={styles.frameTitles}>×{titles}</div>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* THE BADGE — champion steps forward, clean and professional */}
      {selected && (
        <div style={styles.badgeOverlay} onClick={() => setSelected(null)}>
          <div style={styles.badge} onClick={ev => ev.stopPropagation()}>
            <div style={styles.badgeCrest}>
              <Icon name="crown" size={18} color={C.accent} strokeWidth={2} />
            </div>
            <div style={styles.badgeOval}>
              {selected.photoURL ? (
                <img src={selected.photoURL} alt={selected.championName} style={styles.ovalImg} />
              ) : (
                <span style={styles.badgeInitials}>{initials(selected.championName)}</span>
              )}
            </div>
            <div style={styles.badgeName}>{selected.championName}</div>
            <div style={styles.badgeRole}>Bingwa · {selected.tournamentName}</div>
            <div style={styles.badgeDate}>{fmtDate(selected.wonAt)}</div>
            {(titleCounts[selected.championId] || 1) > 1 && (
              <div style={styles.badgeTitles}>Mataji {titleCounts[selected.championId]}</div>
            )}

            {me === selected.championId && (
              <>
                <button
                  style={styles.photoBtn}
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? 'Inapakia…' : selected.photoURL ? 'Badilisha Picha' : 'Weka Picha Yako'}
                </button>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoPick} />
              </>
            )}

            <button style={styles.closeBtn} onClick={() => setSelected(null)}>Funga</button>
          </div>
        </div>
      )}

      <ChessNav />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: C.bgGradient, padding: '20px 0 130px', position: 'relative', overflow: 'hidden' },
  spotlight: {
    position: 'absolute', top: '-160px', left: '50%', transform: 'translateX(-50%)',
    width: '520px', height: '380px', borderRadius: '50%',
    background: 'radial-gradient(ellipse, rgba(52,211,153,0.12), transparent 65%)',
    pointerEvents: 'none',
  },
  bar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px', marginBottom: '20px', position: 'relative' },
  backIconBtn: { width: '38px', height: '38px', borderRadius: '11px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  barTitle: { color: C.text, fontFamily: FONT.display, fontSize: '19px', fontWeight: '800' },
  scrollHint: { display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'flex-end', maxWidth: '620px', margin: '0 auto 8px', padding: '0 18px', color: C.textFaint, fontSize: '10.5px', fontWeight: '700' },
  gallery: {
    display: 'flex', gap: '18px', overflowX: 'auto', padding: '14px 18px 26px',
    scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
  },
  frame: {
    flexShrink: 0, width: '168px', scrollSnapAlign: 'center',
    background: 'linear-gradient(180deg, #262D2A, #1D2320)',
    border: `1px solid ${C.lineStrong}`, borderRadius: '110px 110px 20px 20px',
    padding: '16px 12px 16px', cursor: 'pointer', fontFamily: 'inherit',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
    boxShadow: '0 14px 34px rgba(0,0,0,0.4)', position: 'relative',
    transition: 'transform 0.25s ease, border-color 0.25s ease',
  },
  oval: {
    width: '120px', height: '150px', borderRadius: '50% / 42%',
    background: 'linear-gradient(160deg, #2C3431, #202623)',
    border: '2px solid rgba(52,211,153,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
  },
  ovalImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  ovalInitials: { color: C.accent, fontFamily: FONT.display, fontSize: '34px', fontWeight: '800' },
  frameName: { color: C.text, fontSize: '13.5px', fontWeight: '800', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  frameMeta: { color: C.textFaint, fontSize: '10px', fontWeight: '700', maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  frameTitles: { position: 'absolute', top: '10px', right: '12px', background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.4)', color: C.accent, fontSize: '10px', fontWeight: '800', padding: '2px 8px', borderRadius: '99px' },
  badgeOverlay: {
    position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(10,13,12,0.82)',
    backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '22px',
  },
  badge: {
    width: '100%', maxWidth: '320px', textAlign: 'center',
    background: 'linear-gradient(180deg, #2A302D, #1E2421)',
    border: `1px solid ${C.lineStrong}`, borderRadius: '24px',
    padding: '26px 22px', boxShadow: '0 26px 70px rgba(0,0,0,0.6)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
  },
  badgeCrest: { width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' },
  badgeOval: {
    width: '150px', height: '186px', borderRadius: '50% / 42%',
    background: '#242A27', border: '2.5px solid rgba(52,211,153,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', marginBottom: '12px',
    boxShadow: '0 14px 34px rgba(0,0,0,0.45)',
  },
  badgeInitials: { color: C.accent, fontFamily: FONT.display, fontSize: '46px', fontWeight: '800' },
  badgeName: { color: C.text, fontFamily: FONT.display, fontSize: '21px', fontWeight: '800' },
  badgeRole: { color: C.accent, fontSize: '12px', fontWeight: '800', letterSpacing: '0.3px' },
  badgeDate: { color: C.textFaint, fontSize: '11px', fontWeight: '700', marginTop: '2px' },
  badgeTitles: { marginTop: '8px', background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.35)', color: C.accent, fontSize: '10.5px', fontWeight: '800', padding: '4px 12px', borderRadius: '99px' },
  photoBtn: { marginTop: '14px', background: 'linear-gradient(135deg, #0E9F6E, #34D399)', color: '#06281A', border: 'none', padding: '11px 20px', borderRadius: '11px', fontSize: '12.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  closeBtn: { marginTop: '8px', background: 'transparent', border: 'none', color: C.textDim, fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  empty: { textAlign: 'center', color: C.textDim, padding: '40px 0' },
  emptyCard: { maxWidth: '520px', margin: '0 auto', textAlign: 'center', background: 'rgba(255,255,255,0.04)', border: `1px dashed ${C.lineStrong}`, borderRadius: '18px', padding: '36px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  emptyText: { color: C.textDim, fontSize: '13px', lineHeight: 1.7, maxWidth: '340px' },
  playLink: { color: C.accent, fontSize: '13.5px', fontWeight: '800', textDecoration: 'none' },
};
